import React, { useState, useEffect } from 'react';
import { GraduationCap, Shield, Briefcase, Phone, Compass } from './CustomIcons';

const FEATURES = [
  { Icon: GraduationCap, headline: 'Earn While You Learn', sub: 'Every lesson earns credits toward commissary, call time, and real savings for release.' },
  { Icon: Shield,        headline: 'Blockchain-Verified Credentials', sub: 'Your certifications are permanent, portable, and employer-verified — they can never be lost.' },
  { Icon: Briefcase,     headline: 'Second-Chance Employer Network', sub: 'Matched to real jobs with companies that value skills over history.' },
  { Icon: Phone,         headline: 'Earned Communication', sub: 'Stay connected with family. Your learning unlocks call and video time.' },
  { Icon: Compass,       headline: 'From Cell to Career', sub: 'The only platform where your education follows you home.' },
];

export default function LaunchScreen({ onDone }) {
  const [phase, setPhase] = useState('logo'); // logo | features | exit
  const [featureIdx, setFeatureIdx] = useState(0);
  const [featureIn, setFeatureIn] = useState(false);
  const [showSkip, setShowSkip] = useState(false);
  const [exiting, setExiting] = useState(false);

  const handleDone = () => {
    if (exiting) return;
    setExiting(true);
    sessionStorage.setItem('aos_launch_v1', '1');
    setTimeout(onDone, 550);
  };

  useEffect(() => {
    const timers = [];
    timers.push(setTimeout(() => setShowSkip(true), 2000));
    timers.push(setTimeout(() => { setPhase('features'); setTimeout(() => setFeatureIn(true), 50); }, 3000));

    for (let i = 1; i < 5; i++) {
      timers.push(setTimeout(() => {
        setFeatureIn(false);
        setTimeout(() => { setFeatureIdx(i); setFeatureIn(true); }, 300);
      }, 3000 + i * 1400));
    }

    timers.push(setTimeout(() => { setPhase('exit'); }, 10000));
    timers.push(setTimeout(handleDone, 11500));
    return () => timers.forEach(clearTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { Icon, headline, sub } = FEATURES[featureIdx];

  return (
    <div
      role="dialog"
      aria-label="Assistiv OS introduction"
      aria-live="polite"
      style={{
        position: 'fixed', inset: 0, background: '#111111', zIndex: 9999,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Plus Jakarta Sans, sans-serif', overflow: 'hidden',
        opacity: exiting ? 0 : 1,
        transition: exiting ? 'opacity 0.55s ease' : 'none',
      }}
    >
      {/* Logo section */}
      <div style={{
        textAlign: 'center', position: 'relative', zIndex: 1,
        transform: (phase === 'features' || phase === 'exit') ? 'translateY(-72px)' : 'translateY(0)',
        transition: 'transform 0.85s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      }}>
        {/* Glow behind logo */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          width: '180px', height: '180px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(249,115,22,0.28) 0%, transparent 70%)',
          animation: 'launchGlow 2.8s ease-out 0.6s 1 both',
          pointerEvents: 'none',
        }} />
        <div style={{ animation: 'launchLogoIn 1s cubic-bezier(0.34, 1.56, 0.64, 1) both' }}>
          <img
            src="/logo.png"
            alt="Assistiv OS"
            style={{ height: '88px', width: 'auto', display: 'block', margin: '0 auto' }}
          />
        </div>
        <p style={{
          color: '#555555', fontSize: '11px', marginTop: '16px', letterSpacing: '0.1em',
          fontFamily: 'Inter, sans-serif', textTransform: 'uppercase',
          animation: 'launchFadeIn 0.7s ease-out 1.1s both',
        }}>
          Powered by Tech Tradesmith Institute
        </p>
      </div>

      {/* Feature highlight */}
      {phase === 'features' && (
        <div
          aria-live="polite"
          style={{
            position: 'absolute',
            top: '55%',
            left: '50%',
            width: '100%',
            maxWidth: '440px',
            padding: '0 28px',
            textAlign: 'center',
            opacity: featureIn ? 1 : 0,
            transform: `translateX(-50%) translateY(${featureIn ? 0 : 14}px)`,
            transition: 'opacity 0.3s ease, transform 0.3s ease',
          }}
        >
          <div style={{
            width: '54px', height: '54px', borderRadius: '15px',
            background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px',
          }}>
            <Icon size={22} style={{ color: '#F97316' }} aria-hidden="true" />
          </div>
          <h2 style={{ color: '#FFFFFF', fontSize: '20px', fontWeight: 800, margin: '0 0 10px', letterSpacing: '-0.01em' }}>
            {headline}
          </h2>
          <p style={{ color: '#AAAAAA', fontSize: '14px', lineHeight: 1.65, fontFamily: 'Inter, sans-serif', margin: 0 }}>
            {sub}
          </p>
          {/* Progress dots */}
          <div style={{ display: 'flex', gap: '7px', justifyContent: 'center', marginTop: '26px' }}>
            {FEATURES.map((_, i) => (
              <div key={i} style={{
                height: '4px', borderRadius: '2px',
                width: i === featureIdx ? '22px' : '6px',
                background: i === featureIdx ? '#F97316' : '#2D2D2D',
                transition: 'all 0.35s ease',
              }} />
            ))}
          </div>
        </div>
      )}

      {/* Phase exit — orange line sweep */}
      {phase === 'exit' && (
        <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '2px', zIndex: 2, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            background: 'linear-gradient(90deg, transparent 0%, #F97316 40%, #F59E0B 60%, transparent 100%)',
            animation: 'lineDrawLTR 1.5s ease-in-out forwards',
          }} />
        </div>
      )}

      {/* Skip button */}
      {showSkip && phase !== 'exit' && !exiting && (
        <button
          onClick={handleDone}
          aria-label="Skip intro"
          data-testid="launch-skip-btn"
          style={{
            position: 'absolute', bottom: '28px', right: '28px',
            background: 'transparent', border: '1px solid #282828',
            color: '#4D4D4D', fontSize: '12px', padding: '7px 16px',
            borderRadius: '7px', cursor: 'pointer',
            fontFamily: 'Inter, sans-serif', letterSpacing: '0.03em',
            animation: 'launchFadeIn 0.5s ease-out both',
            transition: 'border-color 0.2s ease, color 0.2s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#F97316'; e.currentTarget.style.color = '#F97316'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#282828'; e.currentTarget.style.color = '#4D4D4D'; }}
        >
          Skip intro
        </button>
      )}
    </div>
  );
}
